// import VideoAnalysis from "@/pages/VideoAnalysis";
import { Navigate, PathRouteProps } from 'react-router-dom';

import HasPermission from '@/components/Middlewares/HasPermission';
import AssignTopics from '@/pages/AssignTopics';
import DistributionHeatmap from '@/pages/DistributionHeatmap';
import DistributionPredictions from '@/pages/DistributionPredictions';
import DistributionProjectSummary from '@/pages/DistributionProjectSummary';
import DistributionSchedule from '@/pages/DistributionSchedule';
import Employees from '@/pages/DistributionSettings/Employees';
import Groups from '@/pages/DistributionSettings/Groups';
import Watermarks from '@/pages/DistributionSettings/Watermarks';
import DistributionTipsLibrary from '@/pages/DistributionTipsLibrary';
import DistributionVideoSummary from '@/pages/DistributionVideoSummary';
import Finances from '@/pages/Finances';
import MLBacklog from '@/pages/MLBacklog';
import MLLanguage from '@/pages/MLLanguage';
import MLLanguages from '@/pages/MLLanguages';
import MLProject from '@/pages/MLProject';
import MLProjects from '@/pages/MLProjects';
import MLTranslation from '@/pages/MLTranslation';
import NetworkPerformance from '@/pages/NetworkPerformance';
import NotFound from '@/pages/NotFound';
import OptimisationOverview from '@/pages/OptimisationOverview';
import OptimisationProject from '@/pages/OptimisationProject';
import Performance from '@/pages/Performance';
import PngExtractor from '@/pages/PngExtractor';
import PreProduction from '@/pages/PreProduction';
import PreProductionProject from '@/pages/PreProductionProject';
import ProductionEvent from '@/pages/ProductionEvent';
import ProductionEvents from '@/pages/ProductionEvents';
import ProductionSettings from '@/pages/ProductionSettings';
import Reports from '@/pages/Reports';
import SingleGroup from '@/pages/Reports/SingleGroup';
import SingleReport from '@/pages/Reports/SingleReport';
import SettingsPage from '@/pages/Settings';
import StrategyChangelog from '@/pages/StrategyChangelog';
import SubtitlesGenerationProjectPage from '@/pages/SubtitlesGenerationProject';
import SubtitlesGenerationProjects from '@/pages/SubtitlesGenerationProjects';
import SubtitlesGenerationPrompts from '@/pages/SubtitlesGenerationPrompts';
import TestingSettings from '@/pages/TestingSettings';
import ThumbnailsGenerator from '@/pages/ThumbnailsGenerator';
import TopThumbnails from '@/pages/TopThumbnails';
import TopTopics from '@/pages/TopTopics';
import TopVideos from '@/pages/TopVideos';

import {
  MLProjectStatus,
  MLProjectTranslationStatus,
  ReleaseStatusEnum,
} from './enums';

export interface Route extends PathRouteProps {
  title?: string;
  routes?: Route[];
  subtitle?: string;
}

const routes: Route[] = [
  // {
  //   path: "video-analysis",
  //   element: <VideoAnalysis />,
  //   title: "Published Videos",
  // },
  {
    path: 'settings',
    element: <SettingsPage />,
    title: 'Settings',
  },
  {
    path: 'performance',
    element: <Performance />,
    title: 'Performance',
  },
  {
    path: 'finances',
    element: <Finances />,
    title: 'Finances',
  },
  {
    path: 'strategy-changelog',
    element: <StrategyChangelog />,
    title: 'Strategy Changelog',
  },
  {
    path: 'assign-topics',
    element: <AssignTopics />,
    title: 'Assign Topics',
  },
  {
    path: 'top-script-topics',
    element: <TopTopics category="script" />,
    title: 'Top Script Topics',
  },
  {
    path: 'top-thumb-topics',
    element: <TopTopics category="thumb" />,
    title: 'Top Thumb Topics',
  },
  {
    path: 'top-title-topics',
    element: <TopTopics category="title" />,
    title: 'Top Title Topics',
  },
  {
    path: 'top-video-types',
    element: <TopTopics category="type" />,
    title: 'Top Video Types',
  },

  {
    path: 'top-thumbnails',
    element: <TopThumbnails />,
    title: 'Top Thumbnails',
  },
  {
    path: 'top-videos',
    element: <TopVideos />,
    title: 'Top Videos',
  },
  {
    path: 'optimisation-projects',
    element: (
      <HasPermission permissions={['use youtube testing']} forRoute>
        <OptimisationOverview />
      </HasPermission>
    ),
    title: 'Optimisation',
  },
  {
    path: 'optimisation-projects/:id',
    element: <OptimisationProject />,
    title: 'Single Optimisation Page',
  },
  {
    path: 'testing-settings',
    element: <TestingSettings />,
    title: 'Testing Settings',
  },
  {
    path: 'reports/facebook',
    element: <NetworkPerformance network="facebook" />,
    title: 'Facebook reports',
  },
  {
    path: 'reports/youtube',
    element: <NetworkPerformance network="youtube" />,
    title: 'Youtube reports',
  },
  {
    path: 'reports/cross-platform',
    element: <NetworkPerformance network="cross-platform" />,
    title: 'Cross Platform reports',
  },
  {
    path: 'research-report',
    element: <NetworkPerformance network="research report" />,
    title: 'Research Report',
  },
  {
    path: 'reports/testing-report',
    element: <NetworkPerformance network="testing report" />,
    title: 'Testing Report',
  },
  {
    path: 'reports/market-share',
    element: <NetworkPerformance network="market share" />,
    title: 'Market share',
  },
  {
    path: 'reports/third-party-report',
    element: <NetworkPerformance network="Third party report" />,
    title: 'Third party Report',
  },
  {
    path: 'reports/third-party-test-report',
    element: <NetworkPerformance network="Third party test report" />,
    title: 'Third party test Report',
  },
  {
    path: 'reports/recommendations-report',
    element: <NetworkPerformance network="recommendations" />,
    title: 'Recommendations',
  },
  {
    path: 'reports/top-100-fb-reels',
    element: <NetworkPerformance network="top 100 fb reels" />,
    title: 'Recommendations',
  },
  {
    path: 'reports/newsflare-bonus',
    element: <NetworkPerformance network="newsflare bonus" />,
    title: 'Newsflare Bonus',
  },
  {
    path: 'reports/amazon',
    element: <NetworkPerformance network="amazon" />,
    title: 'Amazon',
  },
  {
    path: 'reports/employee-engagement',
    element: (
      <HasPermission permissions={['view employee engagement']} forRoute>
        <NetworkPerformance network="employee engagement survey" />
      </HasPermission>
    ),
    title: 'Employee Engagement Survey',
  },
  {
    path: 'reports/facebook-competitors',
    element: <NetworkPerformance network="facebook competitors" />,
    title: 'Facebook Competitors',
  },
  {
    path: 'reports/wow',
    element: <NetworkPerformance network="wow" />,
    title: 'Wow Reports',
  },
  {
    path: 'reports/caters-media',
    element: <NetworkPerformance network="caters-media" />,
    title: 'Caters Media',
  },
  {
    path: 'reports/other',
    element: <Reports />,
  },
  {
    path: 'reports/other/:groupId',
    element: <SingleGroup />,
  },
  {
    path: 'reports/other/:groupId/:reportId',
    element: <SingleReport />,
  },

  {
    path: 'distribution/schedule',
    element: <DistributionSchedule />,
    title: 'Distribution Schedule',
  },
  {
    path: 'distribution/prepared',
    element: <DistributionSchedule status={ReleaseStatusEnum.PREPARED} />,
    title: 'Distribution Prepared',
  },
  {
    path: 'distribution/draft',
    element: <DistributionSchedule status={ReleaseStatusEnum.DRAFT} />,
    title: 'Distribution Draft',
  },
  {
    path: 'distribution/predictions',
    element: <DistributionPredictions />,
    title: 'Distribution Predictions',
  },
  {
    path: 'distribution/conditional-reposts',
    element: <DistributionSchedule withRepostsOnly />,
    title: 'Distribution Draft',
  },
  {
    path: 'distribution/projects',
    element: <DistributionProjectSummary />,
    title: 'Distribution Projects Summary',
  },
  {
    path: 'distribution/projects/:projectId',
    element: <DistributionVideoSummary isProjectSummary />,
    title: 'Distribution Video Summary',
  },
  {
    path: 'distribution/videos',
    element: <DistributionVideoSummary />,
    title: 'Distribution Video Summary',
  },
  {
    path: 'distribution/videos/:status',
    element: <DistributionVideoSummary />,
    title: 'Distribution Video Summary',
  },
  {
    path: 'distribution/heatmap',
    element: <DistributionHeatmap />,
    title: 'Distribution Heatmap',
  },

  {
    path: 'distribution/settings/employees',
    element: <Employees />,
    title: 'Distribution Settings',
  },
  {
    path: 'distribution/settings/watermarks',
    element: <Watermarks />,
    title: 'Distribution Settings',
  },
  {
    path: 'distribution/settings/groups',
    element: <Groups />,
    title: 'Distribution Settings',
  },
  {
    path: 'distribution/tips',
    element: <DistributionTipsLibrary />,
    title: 'Tips Library',
  },
  {
    path: 'distribution/subtitles-generator',
    element: <Navigate to="pending" replace />,
    title: 'Subtitles Generator',
  },
  {
    path: 'distribution/subtitles-generator/prompts',
    element: <SubtitlesGenerationPrompts />,
    title: 'Subtitles Generator Prompts',
  },
  {
    path: 'distribution/subtitles-generator/:status',
    element: <SubtitlesGenerationProjects />,
    title: 'Subtitles Generator',
  },
  {
    path: 'distribution/subtitles-generator/:status/:projectId',
    element: <SubtitlesGenerationProjectPage />,
    title: 'Subtitles Generation Project',
  },
  {
    path: 'distribution/ml-projects',
    element: <Navigate to="pending" replace />,
  },
  {
    path: 'distribution/ml-projects/pending',
    element: <MLProjects status={MLProjectStatus.PENDING} />,
    title: 'Multi-language Projects',
  },
  {
    path: 'distribution/ml-projects/draft',
    element: <MLProjects status={MLProjectStatus.DRAFT} />,
    title: 'Multi-language Projects',
  },
  {
    path: 'distribution/ml-projects/done',
    element: <MLProjects status={MLProjectStatus.DONE} />,
    title: 'Multi-language Projects',
  },
  {
    path: 'distribution/ml-projects/:projectId',
    element: <MLProject />,
    title: 'Multi-language Project',
  },
  {
    path: 'distribution/ml-languages',
    element: <MLLanguages />,
  },
  {
    path: 'distribution/ml-languages/:language',
    element: <Navigate to="pending" replace />,
  },
  {
    path: 'distribution/ml-languages/:language/pending',
    element: <MLLanguage status={MLProjectTranslationStatus.PENDING} />,
  },
  {
    path: 'distribution/ml-languages/:language/done',
    element: <MLLanguage status={MLProjectTranslationStatus.DONE} />,
  },
  {
    path: 'distribution/ml-translations/:translationId',
    element: <MLTranslation />,
  },
  {
    path: 'distribution/ml-backlog',
    element: <MLBacklog />,
  },
  {
    path: 'thumbnails-generator',
    element: <ThumbnailsGenerator />,
    title: 'Thumbnails Generator',
  },
  {
    path: 'png-extractor',
    element: <PngExtractor />,
    title: 'PNG Extractor',
  },
  {
    path: 'pre-production',
    element: <PreProduction />,
    title: 'Pre - Production',
  },
  {
    path: 'pre-production/:projectId',
    element: <PreProductionProject />,
    title: 'Pre - Production Project',
  },
  {
    path: 'production-settings',
    element: <ProductionSettings />,
    title: 'Production Settings',
  },
  {
    path: 'production/events/:status',
    element: <ProductionEvents />,
    title: 'Events',
  },
  {
    path: 'production/event/:id',
    element: <ProductionEvent />,
    title: 'Event',
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;
